import {
  CheckboxWithLabel,
  InputWithLabel,
  SelectWithLabel
} from '@adotel/components'
import { useQuery } from '@apollo/client'
import { IconButton, MenuItem, Typography } from '@material-ui/core'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import React, { useState } from 'react'
import { toast } from 'react-toastify'

import SectionTitle from '../../components/SectionTitle'
import { PMS_TYPE_NAMES_QUERY } from '../../graphql/pmstypes'
import { errorToastMessage } from '../../helpers/errorToast'
import { getTranslate, lang } from '../../localization'
import {
  PMSCommunicationModeType,
  PMSTypeName,
  ProviderAdapter
} from '../../types/accommodation'
import {
  ProviderConnectionsItemBoolean,
  ProviderConnectionsType,
  ProviderSubmit
} from '../../types/accommodationProviderSubmit'
import BookingNotesSwitch, {
  bookingNotesEnabledForAdapter
} from './BookingNotesSwitch'
import useStyles from './Styles/ProviderConnections.style'

type FieldErrors = Record<string, string[]>

function validateProviderConnections(
  connections: ProviderConnectionsType
): FieldErrors {
  const fields = [
    { name: 'availability', item: connections.availability },
    { name: 'rate', item: connections.rate },
    { name: 'restriction', item: connections.restriction },
    { name: 'booking', item: connections.booking }
  ]

  const errors: FieldErrors = {
    availability: [],
    rate: [],
    restriction: [],
    booking: []
  }

  // Check pairwise combinations
  for (let i = 0; i < fields.length; i++) {
    for (let j = i + 1; j < fields.length; j++) {
      const a = fields[i]
      const b = fields[j]

      if (a.item.providerAdapter === b.item.providerAdapter) {
        if (a.item.providerURL !== b.item.providerURL) {
          const error = `${getTranslate(
            'providerAdapter is the same'
          )} ("${getTranslate(a.item.providerAdapter)}") ${getTranslate(
            'for'
          )} ${getTranslate(a.name)} ${getTranslate('and')} ${getTranslate(
            b.name
          )}.`
          errors[a.name].push(error)
          errors[b.name].push(error)
        }
      }
    }
  }

  return errors
}

interface ProviderConnectionsProps {
  selectedProviderSubmit: ProviderSubmit[]
  setSelectedProviderSubmit: React.Dispatch<
    React.SetStateAction<ProviderSubmit[]>
  >
  selectedProviderSubmitIndex: number
  valuesBlur: ProviderConnectionsItemBoolean
  setValuesBlur: React.Dispatch<
    React.SetStateAction<ProviderConnectionsItemBoolean>
  >
  regexUrl: (url: string) => boolean
}

export default function ProviderConnections({
  selectedProviderSubmit,
  setSelectedProviderSubmit,
  selectedProviderSubmitIndex,
  valuesBlur,
  setValuesBlur,
  regexUrl
}: ProviderConnectionsProps) {
  const classes = useStyles()
  const [clickOnConnections, setClickOnConnections] = useState(false)
  const [pmsTypes, setPMSTypes] = useState<PMSTypeName[]>([])

  // A failure here left pmsTypes as [] and the adapter selector simply rendered
  // empty, with nothing said. That is how a 403 -- the resolver had no IAM
  // endpoint-permission mapping, and Layer 1 fails closed on an unmapped endpoint --
  // was reported and triaged as missing data rather than as an authorization error.
  // An empty list and a denied request must not look the same.
  //
  // Routed through errorToastMessage, not error.message: it maps FORBIDDEN to "You do
  // not have permission to perform this action" -- which is what this failure actually
  // was -- and keeps raw resolver text, which may carry identifiers, off the screen.
  const {} = useQuery<{
    pmsTypeNames: PMSTypeName[]
  }>(PMS_TYPE_NAMES_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (res) => {
      setPMSTypes(res.pmsTypeNames)
    },
    onError: (error) => {
      toast.error(errorToastMessage(error))
    }
  })

  const validAdapters = selectedProviderSubmit[
    selectedProviderSubmitIndex
  ].ARIRetrievals.map((ele) => ele.adapter)

  const PMSCommunicationMode: PMSCommunicationModeType[] = [
    'RiskEngineBased',
    'AlwaysSync',
    'AlwaysAsync'
  ]

  const messagesFieldRequired = getTranslate('This field is required')
  const messagesURL = getTranslate('Enter a valid address with https://')

  const providerFieldErrors = validateProviderConnections(
    selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
  )

  const specialAdapters = ['Panel']

  const getFieldErrorState = (
    field: 'availability' | 'rate' | 'restriction' | 'booking'
  ) => {
    const fieldBlurKey = `providerURL${
      field.charAt(0).toUpperCase() + field.slice(1)
    }` as keyof ProviderConnectionsItemBoolean

    const fieldConnection =
      selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections[
        field
      ]

    let basicError = ''

    const isSpecialAdapter = specialAdapters.includes(
      fieldConnection.providerAdapter
    )

    if (!isSpecialAdapter) {
      if (valuesBlur[fieldBlurKey] && !fieldConnection.providerURL) {
        basicError = messagesFieldRequired
      } else if (
        fieldConnection.providerURL &&
        !regexUrl(fieldConnection.providerURL)
      ) {
        basicError = messagesURL
      }
    }

    const fieldErrors = providerFieldErrors[field]
    const fieldValidationError =
      fieldErrors && fieldErrors.length > 0 ? fieldErrors.join(' ') : ''

    const finalHelperText = basicError || fieldValidationError

    return {
      error: !!basicError || !!fieldValidationError,
      helpertext: finalHelperText
    }
  }

  const availabilityErrorState = getFieldErrorState('availability')
  const rateErrorState = getFieldErrorState('rate')
  const restrictionErrorState = getFieldErrorState('restriction')
  const bookingErrorState = getFieldErrorState('booking')

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <SectionTitle title="Connections" marginBottom />
        <IconButton
          className={classes.iconButton}
          onClick={() => {
            setClickOnConnections(!clickOnConnections)
          }}>
          {clickOnConnections ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>
      {clickOnConnections && (
        <div className={classes.inputsProviderConnections}>
          <div className={classes.availabilitySection}>
            <div className={classes.title}>
              <Typography variant="h4">
                {getTranslate('Availability')}
              </Typography>
            </div>
            <div>
              <SelectWithLabel
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerConnections.availability.providerAdapter
                }
                onChange={(e) =>
                  setSelectedProviderSubmit([
                    ...selectedProviderSubmit.slice(
                      0,
                      selectedProviderSubmitIndex
                    ),
                    {
                      ...selectedProviderSubmit[selectedProviderSubmitIndex],
                      providerConnections: {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex]
                          .providerConnections,
                        availability: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections.availability,
                          providerAdapter: e.target.value
                        }
                      }
                    },
                    ...selectedProviderSubmit.slice(
                      selectedProviderSubmitIndex + 1
                    )
                  ])
                }
                size="small"
                label={getTranslate('Provider Adapter')}
                fullWidth>
                {pmsTypes
                  .filter((ele) =>
                    validAdapters.length > 0 && validAdapters[0] !== ''
                      ? validAdapters.includes(ele.EN as ProviderAdapter)
                      : true
                  )
                  .map((item, index) => (
                    <MenuItem
                      key={index}
                      value={item.EN}
                      classes={{
                        root: lang === 'fa' ? classes.menuItem : undefined
                      }}>
                      {lang === 'fa' ? item.FA : item.EN}
                    </MenuItem>
                  ))}
              </SelectWithLabel>
              <InputWithLabel
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerConnections.availability.providerURL
                }
                inputSize="small"
                label={getTranslate('Provider URL')}
                fullWidth
                onChange={(e) =>
                  setSelectedProviderSubmit([
                    ...selectedProviderSubmit.slice(
                      0,
                      selectedProviderSubmitIndex
                    ),
                    {
                      ...selectedProviderSubmit[selectedProviderSubmitIndex],
                      providerConnections: {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex]
                          .providerConnections,
                        availability: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections.availability,
                          providerURL: e.currentTarget.value
                        }
                      }
                    },
                    ...selectedProviderSubmit.slice(
                      selectedProviderSubmitIndex + 1
                    )
                  ])
                }
                onBlur={() => {
                  setValuesBlur({
                    ...valuesBlur,
                    providerURLAvailability: true
                  })
                }}
                error={availabilityErrorState.error}
                helpertext={availabilityErrorState.helpertext}
              />
              <div className={classes.mainCheckBox}>
                <CheckboxWithLabel
                  inputSize="small"
                  checked={
                    selectedProviderSubmit[selectedProviderSubmitIndex]
                      .providerConnections.availability.manualARR
                  }
                  onChange={(e) =>
                    setSelectedProviderSubmit([
                      ...selectedProviderSubmit.slice(
                        0,
                        selectedProviderSubmitIndex
                      ),
                      {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex],
                        providerConnections: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections,
                          availability: {
                            ...selectedProviderSubmit[
                              selectedProviderSubmitIndex
                            ].providerConnections.availability,
                            manualARR: e.currentTarget.checked
                          }
                        }
                      },
                      ...selectedProviderSubmit.slice(
                        selectedProviderSubmitIndex + 1
                      )
                    ])
                  }
                />
                <Typography>{getTranslate('Manual ARR')}</Typography>
              </div>
              <div className={classes.mainCheckBox}>
                <CheckboxWithLabel
                  inputSize="small"
                  checked={
                    selectedProviderSubmit[selectedProviderSubmitIndex]
                      .providerConnections.availability.pmsConnected
                  }
                  onChange={(e) =>
                    setSelectedProviderSubmit([
                      ...selectedProviderSubmit.slice(
                        0,
                        selectedProviderSubmitIndex
                      ),
                      {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex],
                        providerConnections: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections,
                          availability: {
                            ...selectedProviderSubmit[
                              selectedProviderSubmitIndex
                            ].providerConnections.availability,
                            pmsConnected: e.currentTarget.checked
                          }
                        }
                      },
                      ...selectedProviderSubmit.slice(
                        selectedProviderSubmitIndex + 1
                      )
                    ])
                  }
                />
                <Typography>{getTranslate('PMS Connected')}</Typography>
              </div>
            </div>
          </div>
          <div className={classes.lineSection}>
            <div className={classes.lineSectionContainer} />
          </div>
          <div className={classes.ratesSection}>
            <div className={classes.title}>
              <Typography variant="h4">{getTranslate('Rates')}</Typography>
            </div>
            <div>
              <SelectWithLabel
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerConnections.rate.providerAdapter
                }
                onChange={(e) =>
                  setSelectedProviderSubmit([
                    ...selectedProviderSubmit.slice(
                      0,
                      selectedProviderSubmitIndex
                    ),
                    {
                      ...selectedProviderSubmit[selectedProviderSubmitIndex],
                      providerConnections: {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex]
                          .providerConnections,
                        rate: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections.rate,
                          providerAdapter: e.target.value
                        }
                      }
                    },
                    ...selectedProviderSubmit.slice(
                      selectedProviderSubmitIndex + 1
                    )
                  ])
                }
                size="small"
                label={getTranslate('Provider Adapter')}
                fullWidth>
                {pmsTypes
                  .filter((ele) =>
                    validAdapters.length > 0 && validAdapters[0] !== ''
                      ? validAdapters.includes(ele.EN as ProviderAdapter)
                      : true
                  )
                  .map((item, index) => (
                    <MenuItem
                      key={index}
                      value={item.EN}
                      classes={{
                        root: lang === 'fa' ? classes.menuItem : undefined
                      }}>
                      {lang === 'fa' ? item.FA : item.EN}
                    </MenuItem>
                  ))}
              </SelectWithLabel>
              <InputWithLabel
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerConnections.rate.providerURL
                }
                inputSize="small"
                label={getTranslate('Provider URL')}
                fullWidth
                onChange={(e) =>
                  setSelectedProviderSubmit([
                    ...selectedProviderSubmit.slice(
                      0,
                      selectedProviderSubmitIndex
                    ),
                    {
                      ...selectedProviderSubmit[selectedProviderSubmitIndex],
                      providerConnections: {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex]
                          .providerConnections,
                        rate: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections.rate,
                          providerURL: e.currentTarget.value
                        }
                      }
                    },
                    ...selectedProviderSubmit.slice(
                      selectedProviderSubmitIndex + 1
                    )
                  ])
                }
                onBlur={() => {
                  setValuesBlur({
                    ...valuesBlur,
                    providerURLRates: true
                  })
                }}
                error={rateErrorState.error}
                helpertext={rateErrorState.helpertext}
              />
              <div className={classes.mainCheckBox}>
                <CheckboxWithLabel
                  inputSize="small"
                  checked={
                    selectedProviderSubmit[selectedProviderSubmitIndex]
                      .providerConnections.rate.manualARR
                  }
                  onChange={(e) =>
                    setSelectedProviderSubmit([
                      ...selectedProviderSubmit.slice(
                        0,
                        selectedProviderSubmitIndex
                      ),
                      {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex],
                        providerConnections: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections,
                          rate: {
                            ...selectedProviderSubmit[
                              selectedProviderSubmitIndex
                            ].providerConnections.rate,
                            manualARR: e.currentTarget.checked
                          }
                        }
                      },
                      ...selectedProviderSubmit.slice(
                        selectedProviderSubmitIndex + 1
                      )
                    ])
                  }
                />
                <Typography>{getTranslate('Manual ARR')}</Typography>
              </div>
              <div className={classes.mainCheckBox}>
                <CheckboxWithLabel
                  inputSize="small"
                  checked={
                    selectedProviderSubmit[selectedProviderSubmitIndex]
                      .providerConnections.rate.pmsConnected
                  }
                  onChange={(e) =>
                    setSelectedProviderSubmit([
                      ...selectedProviderSubmit.slice(
                        0,
                        selectedProviderSubmitIndex
                      ),
                      {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex],
                        providerConnections: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections,
                          rate: {
                            ...selectedProviderSubmit[
                              selectedProviderSubmitIndex
                            ].providerConnections.rate,
                            pmsConnected: e.currentTarget.checked
                          }
                        }
                      },
                      ...selectedProviderSubmit.slice(
                        selectedProviderSubmitIndex + 1
                      )
                    ])
                  }
                />
                <Typography>{getTranslate('PMS Connected')}</Typography>
              </div>
              <div className={classes.mainCheckBox}>
                <CheckboxWithLabel
                  inputSize="small"
                  checked={
                    selectedProviderSubmit[selectedProviderSubmitIndex]
                      .providerConnections.rate.readRatePlanDataFromPMS
                  }
                  onChange={(e) =>
                    setSelectedProviderSubmit([
                      ...selectedProviderSubmit.slice(
                        0,
                        selectedProviderSubmitIndex
                      ),
                      {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex],
                        providerConnections: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections,
                          rate: {
                            ...selectedProviderSubmit[
                              selectedProviderSubmitIndex
                            ].providerConnections.rate,
                            readRatePlanDataFromPMS: e.currentTarget.checked
                          }
                        }
                      },
                      ...selectedProviderSubmit.slice(
                        selectedProviderSubmitIndex + 1
                      )
                    ])
                  }
                />
                <Typography>
                  {getTranslate('Read rate plans from PMS')}
                </Typography>
              </div>
            </div>
          </div>
          <div className={classes.lineSection}>
            <div className={classes.lineSectionContainer} />
          </div>
          <div className={classes.restrictionSection}>
            <div className={classes.title}>
              <Typography variant="h4">
                {getTranslate('Restriction')}
              </Typography>
            </div>
            <div>
              <SelectWithLabel
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerConnections.restriction.providerAdapter
                }
                onChange={(e) =>
                  setSelectedProviderSubmit([
                    ...selectedProviderSubmit.slice(
                      0,
                      selectedProviderSubmitIndex
                    ),
                    {
                      ...selectedProviderSubmit[selectedProviderSubmitIndex],
                      providerConnections: {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex]
                          .providerConnections,
                        restriction: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections.restriction,
                          providerAdapter: e.target.value
                        }
                      }
                    },
                    ...selectedProviderSubmit.slice(
                      selectedProviderSubmitIndex + 1
                    )
                  ])
                }
                size="small"
                label={getTranslate('Provider Adapter')}
                fullWidth>
                {pmsTypes
                  .filter((ele) =>
                    validAdapters.length > 0 && validAdapters[0] !== ''
                      ? validAdapters.includes(ele.EN as ProviderAdapter)
                      : true
                  )
                  .map((item, index) => (
                    <MenuItem
                      key={index}
                      value={item.EN}
                      classes={{
                        root: lang === 'fa' ? classes.menuItem : undefined
                      }}>
                      {lang === 'fa' ? item.FA : item.EN}
                    </MenuItem>
                  ))}
              </SelectWithLabel>
              <InputWithLabel
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerConnections.restriction.providerURL
                }
                inputSize="small"
                label={getTranslate('Provider URL')}
                fullWidth
                onChange={(e) =>
                  setSelectedProviderSubmit([
                    ...selectedProviderSubmit.slice(
                      0,
                      selectedProviderSubmitIndex
                    ),
                    {
                      ...selectedProviderSubmit[selectedProviderSubmitIndex],
                      providerConnections: {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex]
                          .providerConnections,
                        restriction: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections.restriction,
                          providerURL: e.currentTarget.value
                        }
                      }
                    },
                    ...selectedProviderSubmit.slice(
                      selectedProviderSubmitIndex + 1
                    )
                  ])
                }
                onBlur={() => {
                  setValuesBlur({
                    ...valuesBlur,
                    providerURLRestriction: true
                  })
                }}
                error={restrictionErrorState.error}
                helpertext={restrictionErrorState.helpertext}
              />
              <div className={classes.mainCheckBox}>
                <CheckboxWithLabel
                  inputSize="small"
                  checked={
                    selectedProviderSubmit[selectedProviderSubmitIndex]
                      .providerConnections.restriction.manualARR
                  }
                  onChange={(e) =>
                    setSelectedProviderSubmit([
                      ...selectedProviderSubmit.slice(
                        0,
                        selectedProviderSubmitIndex
                      ),
                      {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex],
                        providerConnections: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections,
                          restriction: {
                            ...selectedProviderSubmit[
                              selectedProviderSubmitIndex
                            ].providerConnections.restriction,
                            manualARR: e.currentTarget.checked
                          }
                        }
                      },
                      ...selectedProviderSubmit.slice(
                        selectedProviderSubmitIndex + 1
                      )
                    ])
                  }
                />
                <Typography>{getTranslate('Manual ARR')}</Typography>
              </div>
              <div className={classes.mainCheckBox}>
                <CheckboxWithLabel
                  inputSize="small"
                  checked={
                    selectedProviderSubmit[selectedProviderSubmitIndex]
                      .providerConnections.restriction.pmsConnected
                  }
                  onChange={(e) =>
                    setSelectedProviderSubmit([
                      ...selectedProviderSubmit.slice(
                        0,
                        selectedProviderSubmitIndex
                      ),
                      {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex],
                        providerConnections: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections,
                          restriction: {
                            ...selectedProviderSubmit[
                              selectedProviderSubmitIndex
                            ].providerConnections.restriction,
                            pmsConnected: e.currentTarget.checked
                          }
                        }
                      },
                      ...selectedProviderSubmit.slice(
                        selectedProviderSubmitIndex + 1
                      )
                    ])
                  }
                />
                <Typography>{getTranslate('PMS Connected')}</Typography>
              </div>
              <div className={classes.mainCheckBox}>
                <CheckboxWithLabel
                  inputSize="small"
                  checked={
                    selectedProviderSubmit[selectedProviderSubmitIndex]
                      .providerConnections.restriction.readRatePlanDataFromPMS
                  }
                  onChange={(e) =>
                    setSelectedProviderSubmit([
                      ...selectedProviderSubmit.slice(
                        0,
                        selectedProviderSubmitIndex
                      ),
                      {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex],
                        providerConnections: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections,
                          restriction: {
                            ...selectedProviderSubmit[
                              selectedProviderSubmitIndex
                            ].providerConnections.restriction,
                            readRatePlanDataFromPMS: e.currentTarget.checked
                          }
                        }
                      },
                      ...selectedProviderSubmit.slice(
                        selectedProviderSubmitIndex + 1
                      )
                    ])
                  }
                />
                <Typography>
                  {getTranslate('Read rate plans from PMS')}
                </Typography>
              </div>
            </div>
          </div>
          <div className={classes.lineSection}>
            <div className={classes.lineSectionContainer} />
          </div>
          <div className={classes.bookingSection}>
            <div className={classes.title}>
              <Typography variant="h4">{getTranslate('Booking')}</Typography>
            </div>
            <div>
              <SelectWithLabel
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerConnections.booking.providerAdapter
                }
                onChange={(e) =>
                  setSelectedProviderSubmit([
                    ...selectedProviderSubmit.slice(
                      0,
                      selectedProviderSubmitIndex
                    ),
                    {
                      ...selectedProviderSubmit[selectedProviderSubmitIndex],
                      providerConnections: {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex]
                          .providerConnections,
                        booking: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections.booking,
                          providerAdapter: e.target.value,
                          bookingNotesEnabled: bookingNotesEnabledForAdapter(
                            selectedProviderSubmit[selectedProviderSubmitIndex]
                              .providerConnections.booking,
                            e.target.value as string
                          )
                        }
                      }
                    },
                    ...selectedProviderSubmit.slice(
                      selectedProviderSubmitIndex + 1
                    )
                  ])
                }
                size="small"
                label={getTranslate('Provider Adapter')}
                fullWidth>
                {pmsTypes
                  .filter((ele) =>
                    validAdapters.length > 0 && validAdapters[0] !== ''
                      ? validAdapters.includes(ele.EN as ProviderAdapter)
                      : true
                  )
                  .map((item, index) => (
                    <MenuItem
                      key={index}
                      value={item.EN}
                      classes={{
                        root: lang === 'fa' ? classes.menuItem : undefined
                      }}>
                      {lang === 'fa' ? item.FA : item.EN}
                    </MenuItem>
                  ))}
              </SelectWithLabel>
              <InputWithLabel
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerConnections.booking.providerURL
                }
                inputSize="small"
                label={getTranslate('Provider URL')}
                fullWidth
                onChange={(e) =>
                  setSelectedProviderSubmit([
                    ...selectedProviderSubmit.slice(
                      0,
                      selectedProviderSubmitIndex
                    ),
                    {
                      ...selectedProviderSubmit[selectedProviderSubmitIndex],
                      providerConnections: {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex]
                          .providerConnections,
                        booking: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections.booking,
                          providerURL: e.currentTarget.value
                        }
                      }
                    },
                    ...selectedProviderSubmit.slice(
                      selectedProviderSubmitIndex + 1
                    )
                  ])
                }
                onBlur={() => {
                  setValuesBlur({
                    ...valuesBlur,
                    providerURLBooking: true
                  })
                }}
                error={bookingErrorState.error}
                helpertext={bookingErrorState.helpertext}
              />
              <SelectWithLabel
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerConnections.booking.pmsCommunicationMode
                }
                onChange={(e) =>
                  setSelectedProviderSubmit([
                    ...selectedProviderSubmit.slice(
                      0,
                      selectedProviderSubmitIndex
                    ),
                    {
                      ...selectedProviderSubmit[selectedProviderSubmitIndex],
                      providerConnections: {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex]
                          .providerConnections,
                        booking: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections.booking,
                          pmsCommunicationMode: e.target
                            .value as PMSCommunicationModeType
                        }
                      }
                    },
                    ...selectedProviderSubmit.slice(
                      selectedProviderSubmitIndex + 1
                    )
                  ])
                }
                size="small"
                label={getTranslate('PMS Communication Mode')}
                fullWidth>
                {PMSCommunicationMode.map((pms) => (
                  <MenuItem
                    key={pms}
                    value={pms}
                    classes={{
                      root: lang === 'fa' ? classes.menuItem : undefined
                    }}>
                    {getTranslate(pms)}
                  </MenuItem>
                ))}
              </SelectWithLabel>
              <InputWithLabel
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerConnections.booking.bookingTimeout
                }
                label={getTranslate('Booking TimeOut (in Second)')}
                placeholder={getTranslate('Booking Timeout')}
                autoComplete="off"
                inputSize="small"
                onChange={(e) => {
                  setSelectedProviderSubmit([
                    ...selectedProviderSubmit.slice(
                      0,
                      selectedProviderSubmitIndex
                    ),
                    {
                      ...selectedProviderSubmit[selectedProviderSubmitIndex],
                      providerConnections: {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex]
                          .providerConnections,
                        booking: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections.booking,
                          bookingTimeout:
                            typeof parseInt(e.currentTarget.value, 10) ===
                              'number' &&
                            !isNaN(parseInt(e.currentTarget.value, 10))
                              ? parseInt(e.currentTarget.value, 10)
                              : parseInt('0')
                        }
                      }
                    },
                    ...selectedProviderSubmit.slice(
                      selectedProviderSubmitIndex + 1
                    )
                  ])
                }}
              />
              <InputWithLabel
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerConnections.booking.occupancyPercentThreshold
                }
                label={getTranslate('Occupancy Percent Threshold')}
                placeholder={getTranslate('example: ') + 15}
                autoComplete="off"
                inputSize="small"
                onChange={(e) => {
                  setSelectedProviderSubmit([
                    ...selectedProviderSubmit.slice(
                      0,
                      selectedProviderSubmitIndex
                    ),
                    {
                      ...selectedProviderSubmit[selectedProviderSubmitIndex],
                      providerConnections: {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex]
                          .providerConnections,
                        booking: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections.booking,
                          occupancyPercentThreshold:
                            typeof parseInt(e.currentTarget.value, 10) ===
                              'number' &&
                            !isNaN(parseInt(e.currentTarget.value, 10))
                              ? parseInt(e.currentTarget.value, 10)
                              : parseInt('0')
                        }
                      }
                    },
                    ...selectedProviderSubmit.slice(
                      selectedProviderSubmitIndex + 1
                    )
                  ])
                }}
              />
              <div className={classes.mainCheckBox}>
                <CheckboxWithLabel
                  inputSize="small"
                  checked={
                    selectedProviderSubmit[selectedProviderSubmitIndex]
                      .providerConnections.booking.pmsConnected
                  }
                  onChange={(e) =>
                    setSelectedProviderSubmit([
                      ...selectedProviderSubmit.slice(
                        0,
                        selectedProviderSubmitIndex
                      ),
                      {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex],
                        providerConnections: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections,
                          booking: {
                            ...selectedProviderSubmit[
                              selectedProviderSubmitIndex
                            ].providerConnections.booking,
                            pmsConnected: e.currentTarget.checked,
                            bookingNotesEnabled:
                              e.currentTarget.checked &&
                              bookingNotesEnabledForAdapter(
                                selectedProviderSubmit[
                                  selectedProviderSubmitIndex
                                ].providerConnections.booking,
                                selectedProviderSubmit[
                                  selectedProviderSubmitIndex
                                ].providerConnections.booking.providerAdapter
                              )
                          }
                        }
                      },
                      ...selectedProviderSubmit.slice(
                        selectedProviderSubmitIndex + 1
                      )
                    ])
                  }
                />
                <Typography>{getTranslate('PMS Connected')}</Typography>
              </div>
              <BookingNotesSwitch
                descriptionId={`booking-notes-prerequisite-${selectedProviderSubmitIndex}`}
                connection={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerConnections.booking
                }
                onChange={(enabled) =>
                  setSelectedProviderSubmit((previous) =>
                    previous.map((provider, index) =>
                      index === selectedProviderSubmitIndex
                        ? {
                            ...provider,
                            providerConnections: {
                              ...provider.providerConnections,
                              booking: {
                                ...provider.providerConnections.booking,
                                bookingNotesEnabled: enabled
                              }
                            }
                          }
                        : provider
                    )
                  )
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
